import { useEffect, useState } from 'react';
import any from 'promise.any';
import { notify } from '@lib/bugsnag';
import { NotifiableError } from '@bugsnag/core';
import { AbiItem } from 'web3-utils';
import { BigNumber } from '@ethersproject/bignumber';
import { formatUnits } from '@ethersproject/units';
import { SigningStargateClient } from '@cosmjs/stargate';
import {
  CroChain,
  TokenAsset,
  Wallet,
  WalletType,
  EthChainId,
  CronosChain,
  CosmosChain,
  CosmosHubChain,
  CosmosChainInfo,
} from '@types';
import { getChainInfo as getCosmosChainInfo } from '@lib/cosmos-chain';
import { getChainInfo as getCronosChainInfo } from '@lib/cronos-chain';
import {
  getNativeCurrencySymbolInEthereumNetworks,
  getContractCronosEthereum,
  getAbiCronosEthereum,
  getNativeBalanceInEthNetwork,
  getContractBalanceInEthNetwork,
} from '@lib/utils';
import useNetworkEnv from '@hooks/useNetworkEnv';
import { UnsupportedChainIdError } from '@web3-react/core';

type UseTokenBalanceState = {
  balance: string;
  status: TokenBalanceStatus;
  tokenAsset: TokenAsset | null;
};

export enum TokenBalanceStatus {
  PENDING = 'pending',
  SUCCESS = 'success',
  FAILED = 'failed',
}

const useTokenBalance = (
  wallet: Wallet | null,
  tokenAsset: TokenAsset | null
) => {
  const [isTestnet] = useNetworkEnv();
  const [balanceState, setBalanceState] = useState<UseTokenBalanceState>({
    balance: '',
    status: TokenBalanceStatus.PENDING,
    tokenAsset: tokenAsset,
  });
  const shouldFetchBalance = wallet && tokenAsset;

  const setBalanceSuccess = (
    balance: string,
    tokenAsset: TokenAsset | null
  ) => {
    setBalanceState({
      balance: balance,
      status: TokenBalanceStatus.SUCCESS,
      tokenAsset: tokenAsset,
    });
  };

  const setBalanceFailed = (tokenAsset: TokenAsset | null) => {
    setBalanceState((prev) => ({
      ...prev,
      status: TokenBalanceStatus.FAILED,
      tokenAsset: tokenAsset,
    }));
  };

  const setBalancePending = (tokenAsset: TokenAsset | null) => {
    setBalanceState({
      balance: '',
      status: TokenBalanceStatus.PENDING,
      tokenAsset: tokenAsset,
    });
  };

  useEffect(() => {
    setBalancePending(tokenAsset);
    if (!shouldFetchBalance) return;
    const fetchNativeBalanceInEthereumNetworks = async () => {
      try {
        const chainInfo = getCronosChainInfo(wallet.chain as EthChainId);
        const res = await getNativeBalanceInEthNetwork(wallet, wallet.address);
        setBalanceSuccess(
          formatUnits(BigNumber.from(res), chainInfo.nativeCurrency.decimals),
          tokenAsset
        );
      } catch (e) {
        notify(e as NotifiableError);
        setBalanceFailed(tokenAsset);
      }
    };
    const fetchContractBalanceInEthereumNetworks = async () => {
      try {
        const contractChain = await getContractCronosEthereum(
          wallet,
          tokenAsset,
          isTestnet
        );
        const abi = getAbiCronosEthereum(wallet);
        const res = await getContractBalanceInEthNetwork(
          wallet,
          contractChain,
          abi as AbiItem[],
          wallet.address
        );
        setBalanceSuccess(
          formatUnits(BigNumber.from(res), tokenAsset.decimals),
          tokenAsset
        );
      } catch (e) {
        notify(e as NotifiableError);
        setBalanceFailed(tokenAsset);
      }
    };

    const fetchBalanceInCosmosNetworks = async () => {
      async function fetchBalance(
        wallet: Wallet,
        chainInfo: CosmosChainInfo,
        rpc: string
      ) {
        const client = await SigningStargateClient.connectWithSigner(
          rpc,
          wallet.signer!
        );
        const balance = await client.getBalance(
          wallet.address,
          chainInfo.stakeCurrency.coinMinimalDenom
        );
        return balance;
      }
      try {
        const chainInfo = getCosmosChainInfo(wallet.chain as CosmosChain);
        const balance = await any(
          (chainInfo.bridgeRpcs
            ? [...chainInfo.bridgeRpcs, chainInfo.rpc]
            : [chainInfo.rpc]
          ).map((rpc) => fetchBalance(wallet, chainInfo, rpc))
        );
        setBalanceSuccess(
          formatUnits(balance.amount, chainInfo.stakeCurrency.coinDecimals),
          tokenAsset
        );
      } catch (e) {
        notify(e as NotifiableError);
        setBalanceFailed(tokenAsset);
      }
    };
    switch (wallet.type) {
      case WalletType.metamask:
        if (
          getNativeCurrencySymbolInEthereumNetworks(
            wallet.chain as EthChainId
          ) === tokenAsset.symbol
        ) {
          fetchNativeBalanceInEthereumNetworks();
        } else {
          fetchContractBalanceInEthereumNetworks();
        }
        break;
      case WalletType.keplr:
        fetchBalanceInCosmosNetworks();
        break;
      case WalletType.deficonnect:
        if (Object.values(CroChain).includes(wallet.chain as CroChain)) {
          fetchBalanceInCosmosNetworks();
        } else if (
          Object.values(CosmosHubChain).includes(wallet.chain as CosmosHubChain)
        ) {
          fetchBalanceInCosmosNetworks();
        } else if (
          Object.values(EthChainId).includes(wallet.chain as EthChainId)
        ) {
          if (
            getNativeCurrencySymbolInEthereumNetworks(
              wallet.chain as EthChainId
            ) === tokenAsset.symbol
          ) {
            fetchNativeBalanceInEthereumNetworks();
          } else {
            fetchContractBalanceInEthereumNetworks();
          }
        }
        break;
      default:
        throw new UnsupportedChainIdError(wallet.type);
    }
  }, [wallet, tokenAsset, isTestnet, shouldFetchBalance]);

  return balanceState;
};

export default useTokenBalance;
